/**
 * RichText — рендер Markdown + KaTeX + підсвітка наголосів.
 *
 * Єдине місце, де текст з картки/конспекту перетворюється на React-ноди.
 * Наголоси підсвічуються лише коли їх увімкнено в налаштуваннях
 * (налаштування.highlightStress) — і тільки для карток із тегом #наголоси.
 */

import { useMemo } from 'react'
import { renderMarkdown } from '../lib/markdown.jsx'
import { splitStress, hasStressTag, COMBINING_ACUTE } from '../lib/stress.js'

/**
 * Підсвічує ударні букви в одному шматку тексту.
 * Знаки наголосу прибираються, літера фарбується через .stress-mark.
 */
function renderStressed(text) {
  return splitStress(text).map((part, i) =>
    part.stressed ? (
      <span key={i} className="stress-mark">
        {part.text}
      </span>
    ) : (
      part.text
    ),
  )
}

export default function RichText({
  children,
  tags = [],
  highlightStress = true,
  className = '',
  ...rest
}) {
  const source = String(children ?? '')

  // Підсвітка потрібна, коли: увімкнено в налаштуваннях, є тег #наголоси
  // і в тексті реально стоїть U+0301.
  const wantStress =
    highlightStress && hasStressTag(tags) && source.includes(COMBINING_ACUTE)

  const renderText = useMemo(
    () => (wantStress ? renderStressed : null),
    [wantStress],
  )

  const content = useMemo(
    () => renderMarkdown(source, { renderText }),
    [source, renderText],
  )

  if (content === null) return null

  return (
    <div className={`text-[15px] leading-relaxed ${wantStress ? '' : ''} ${className}`} {...rest}>
      {content}
    </div>
  )
}
